import { css, html, LitElement } from 'lit';
import '../MessageSearchBar/SearchBar';
import '../ChatTile/ChatTile';
import { formatUnixTimestamp } from '../../../utils/unixTimeConverter';
import { getProductImage } from '../../../utils/utils';
import { styleMap } from 'lit/directives/style-map.js';
import './ContextMenu';
import { NoChatPlaceholder } from '../../../assets/svg/NoChatPlaceholder';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { debounce } from '../../../lib/helpers';
import '../../../stories/Components/Loader/Spinner/Spinner';
import '../NewChatSidebar/NewChatSidebar';

class ChatSidebar extends LitElement {
  static properties = {
    conversationData: { type: Array },
    displayTitle: { type: String },
    customTypes: { type: Array },
    onChatTileSelected: { type: Function },
    unreadMessageCount: { type: String },
    tileCount: { type: Number },
    imageHeight: { type: String },
    imageWidth: { type: String },
    imageRadius: { type: String },
    userNameColor: { type: String },
    userNameFontFamily: { type: String },
    userNameFontWeight: { type: String },
    usernameFontSize: { type: String },
    usernameFontStyle: { type: String },
    chatPreviewColor: { type: String },
    chatPreviewFontFamily: { type: String },
    chatPreviewFontWeight: { type: String },
    chatPreviewFontSize: { type: String },
    chatPreviewFontStyle: { type: String },
    unreadCountColor: { type: String },
    unreadCountFontSize: { type: String },
    unreadCountBgColor: { type: String },
    unreadCountBorderRadius: { type: String },
    messageDateColor: { type: String },
    messageDateFontFamily: { type: String },
    messageDateFontWeight: { type: String },
    messageDateFontSize: { type: String },
    messageDateFontStyle: { type: String },
    padding: { type: String },
    usernamePadding: { type: String },
    chatPreviewPadding: { type: String },
    messageDatePadding: { type: String },
    bgColor: { type: String },
    borderWidth: { type: String },
    borderColor: { type: String },
    borderStyle: { type: String },
    height: { type: String },
    width: { type: String },
    searchQuery: { type: String },
    isChatFilter: { type: Boolean },
    selectedCustomType: { type: String },
    isAllChats: { type: Boolean },
    nonBlockedContacts: { type: Array },
    filterTabsContainer: { type: Object },
    filterTab: { type: Object },
    filterTabHover: { type: Object },
    filterTabActive: { type: Object },
    isSidebarHeaderSearch: { type: Boolean },
    isSidebarHeaderLabel: { type: Boolean },
    sidebarHeaderLabel: { type: String },
    sidebarColor: { type: String },
    sidebarSelectedColor: { type: String },
    contextMenuX: { type: Number },
    contextMenuY: { type: Number },
    isContextMenuOpen: { type: Boolean },
    isMqttConnected: { type: Boolean },
    selectedTileForMenu: { type: Object },

    isConfirmationDialogOpen: { type: Boolean },
    confirmationMessage: { type: String },
    confirmationOnConfirm: { type: Function },
    isNoContactsFound: { type: Boolean },
    exitedGroups: { type: Array },
    selectedConversationFromURL: { type: String },
    selectedTileId: { type: String },
    handleSearchData: { type: Function },
    typingConversationId: { type: String },
    isConversationLoading: { type: Boolean },
    isTyping: { type: Boolean },
    typingUserName: { type: String },
    allMessages: { type: Array },
    userMetaData: { type: Object },
    isLoading: { type: Boolean },
    leofferAccountId: { type: String },
    isPreloadingForFilter: { type: Boolean },
    isGroupCreationLoading: { type: Boolean },
    hasMoreConversations: { type: Boolean },
    isPaginationMember: { type: Boolean },
    isLoadingMember: { type: Boolean },
    hasMoreMembers: { type: Boolean },
    renderComponentName: { type: String },
    filteredConversationsFromSearchBar: { type: Array },
    activeTabFromSearchBar: { type: String },
    handleBackButtonClick: {type: Function}
  };

  static styles = css`
    .chat-sidebar-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      box-sizing: border-box;
      padding: 20px;
      gap: 5px;
      position: relative;
    }

    .chat-sidebar-search {
      height: auto;
      padding: 0 10px 0 0;
    }

    .chat-sidebar-list {
      height: 100%;
      max-height: 90%;
      overflow-y: auto;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    chat-tile:hover {
      background-color: var(--bg-secondary-color) !important;
    }

    .chat-sidebar-list::-webkit-scrollbar {
      width: 0px;
    }

    .chat-sidebar-list::-webkit-scrollbar-track {
      background: transparent;
    }
    .chat-sidebar-list::-webkit-scrollbar-thumb {
      background-color: var(--bg-secondary-color) !important;
      border-radius: 10px;
    }

    .search-placeholder {
      display: flex;
      height: 100%;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      flex-direction: column;
      gap: 5px;
    }

    .search-placeholder span:nth-child(2) {
      font-weight: 700;
      font-size: 28px;
    }

    .search-placeholder span:nth-child(3) {
      font-size: 16px;
      font-weight: 400;
      text-align: center;
    }

    .confirmation-dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }

    .confirmation-dialog {
      background-color: var(--bg-primary-color);
      padding: 20px;
      border-radius: 8px;
      min-width: var(--sidebar-width);
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    .confirmation-dialog-message {
      color: var(--text-primary-color);
      margin-bottom: 20px;
      text-align: center;
    }

    .confirmation-dialog-buttons {
      display: flex;
      justify-content: center;
      gap: 10px;
    }

    .confirmation-dialog-button {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    }

    .confirm-button {
      background-color: #dc3545;
      color: white;
    }

    .cancel-button {
      background-color: var(--bg-secondary-color);
      color: var(--text-primary-color);
    }

    .loading-container {
      width: calc(100% - 35px); //because of padding of chat-sidebar-container + scrollbarwidth
      position: absolute;
      bottom: 0;
      left: 0;
      background-color: var(--bg-primary-color);
      padding: 10px;
      margin-right: 15px;
    }

    @media (max-width: 768px) {
      .loading-container {
        width: 100%;
      }
    }
  `;

  constructor() {
    super();
    this.conversationData = [];
    this.customTypes = [];
    this.selectedCustomType = null;
    this.isAllChats = false;
    this.tileCount = 20;
    this.imageHeight = '50px';
    this.imageWidth = '50px';
    this.imageRadius = '5px';
    this.userNameColor = 'var(--text-primary-color)';
    this.userNameFontFamily = 'sans-serif';
    this.userNameFontWeight = '600';
    this.usernameFontSize = '16px';
    this.usernameFontStyle = 'sans-serif';
    this.chatPreviewColor = 'var(--text-secondary-color)';
    this.chatPreviewFontFamily = 'sans-serif';
    this.chatPreviewFontWeight = 'normal';
    this.chatPreviewFontSize = '12px';
    this.chatPreviewFontStyle = 'sans-serif';
    this.unreadCountColor = '#fff';
    this.unreadCountFontSize = '12px';
    this.unreadCountBgColor = '#241172';
    this.unreadCountHeight = '20px';
    this.unreadCountWidth = '20px';
    this.unreadCountBorderRadius = '50%';
    this.messageDateColor = 'var( --text-last-seen) ';
    this.messageDateFontFamily = 'monospace';
    this.messageDateFontWeight = 'normal';
    this.messageDateFontSize = '12px';
    this.messageDateFontStyle = 'italic';
    this.padding = '10px';
    this.usernamePadding = '1px';
    this.chatPreviewPadding = '1px';
    this.messageDatePadding = '1px';
    this.bgColor = '#f9e3ff ';
    this.borderWidth = '1px';
    this.borderColor = 'var(--bg-secondary-color)';
    this.borderStyle = 'solid';
    this.height = 'fit-content';
    this.width = '100%';
    this.searchQuery = '';
    this.contextMenuX = 0;
    this.contextMenuY = 0;
    this.isContextMenuOpen = false;
    this.selectedTileForMenu = null;
    this.isConfirmationDialogOpen = false;
    this.confirmationMessage = '';
    this.confirmationOnConfirm = null;
    this.exitedGroups = [];
    this.renderComponentName = 'Sidebar';
    this.selectedConversationFromURL = this.getConversationIdFromURL();
    this.selectedTileId = this.getConversationIdFromURL();
    this.debouncedSearchHandler = debounce(query => {
      this.handleSearchData(query);
    }, 500);
    this.isLoading = false;
    this.isGroupCreationLoading = false;
    this.filteredConversationsFromSearchBar = null;
    this.activeTabFromSearchBar = 'all';
    this.handleBackButtonClick = () => {}
  }

  firstUpdated() {
    // Called after the component's first render
    this.updateStyles();
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('filterTabsContainer') || changedProperties.has('filterTab') || changedProperties.has('filterTabHover') || changedProperties.has('filterTabActive')) {
      // Schedule style update after render
      this.updateComplete.then(() => this.updateStyles());
    }

    if (changedProperties.has('conversationData')) {
      if (this.isLoading && !this.isGroupCreationLoading) {
        setTimeout(() => {
          this.isLoading = false;
          this.requestUpdate();
        }, 300);
      }
    }
  }


  updateStyles() {
    if (!this.shadowRoot) return;

    const styleElement = document.createElement('style');

    const css = `
      .chat-filter-tabs {
        ${Object.entries(this.filterTabsContainer)
          .map(([key, value]) => `${key}: ${value};`)
          .join('\n')}
      }
      
      .filter-tab {
        ${Object.entries(this.filterTab)
          .map(([key, value]) => `${key}: ${value};`)
          .join('\n')}
      }
      
      .filter-tab:hover {
        ${Object.entries(this.filterTabHover)
          .map(([key, value]) => `${key}: ${value};`)
          .join('\n')}
      }
      
      .filter-tab.active {
        background-color: var(--text-primary-color) !important;
        color: white !important;
        ${Object.entries(this.filterTabActive)
          .map(([key, value]) => `${key}: ${value};`)
          .join('\n')}
      }
    `;

    styleElement.textContent = css;

    const oldStyle = this.shadowRoot.querySelector('style[data-dynamic]');
    if (oldStyle) {
      oldStyle.remove();
    }

    styleElement.setAttribute('data-dynamic', 'true');
    this.shadowRoot.appendChild(styleElement);
  }

  handleSearchInput(e) {
    this.searchQuery = e.detail.value?.toLowerCase();
    this.debouncedSearchHandler(this.searchQuery);
    this.requestUpdate();
  }

  handleFilteredConversationsChanged(e) {
  
    const { activeTab, filteredConversations } = e.detail;
    this.filteredConversationsFromSearchBar = filteredConversations;
    this.activeTabFromSearchBar = activeTab;
    this.requestUpdate();
  }

  handleTabClick(customType) {
    this.selectedCustomType = this.selectedCustomType === customType ? null : customType;
    this.isAllChats = customType === 'all';
    this.requestUpdate();
  }

  applyHoverStyles(e) {
    Object.assign(e.target.style, this.filterTabHover);
  }

  removeHoverStyles(e) {
    // Reset to the default filterTab styles or active styles if it's active
    const isActive = e.target.classList.contains('active');
    Object.assign(e.target.style, isActive ? this.filterTabActive : this.filterTab);
  }

  handleRightClick(e, tile) {
    e.preventDefault();
    this.contextMenuX = e.clientX;
    this.contextMenuY = e.clientY;
    this.isContextMenuOpen = true;
    this.selectedTileForMenu = tile;
    this.requestUpdate();
  }

  handleMenuItemSelected(e) {
    const { action } = e.detail;
    switch (action) {
      case 'delete':
        const isExitedGroup = this.exitedGroups.includes(this.selectedTileForMenu?.conversationId);

        if (this.selectedTileForMenu?.isGroup && !isExitedGroup) {
          // Handle group exit
          const tileToDelete = this.selectedTileForMenu;
          this.isConfirmationDialogOpen = true;
          this.confirmationMessage = 'Are you sure you want to exit this group?';

          this.confirmationOnConfirm = () => {
            const conversationId = tileToDelete?.conversationId;
            this.dispatchEvent(
              new CustomEvent('exit-group', {
                detail: {
                  conversationId: tileToDelete.conversationId,
                },
                bubbles: true,
                composed: true,
              }),
            );
            // After successful exit, add to exitedGroups
            this.handleGroupExitSuccess(conversationId);
            this.isConfirmationDialogOpen = false;
            this.isContextMenuOpen = false;
          };
        } else {
          // Regular delete for non-group chats or exited groups
          this.dispatchEvent(
            new CustomEvent('delete-conversation', {
              detail: {
                conversationId: this.selectedTileForMenu.conversationId,
                isGroup: this.selectedTileForMenu?.isGroup || false,
              },
              bubbles: true,
              composed: true,
            }),
          );
        }
        break;
    }
  }

  handleScroll(event) {
    const element = event.target;
    const threshold = 10;

    if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold && this.hasMoreConversations) {
      this.isLoading = true;
      this.dispatchEvent(
        new CustomEvent('chat-tile-list-scrolled-to-bottom', {
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  handleGroupExitSuccess(conversationId) {
    this.exitedGroups = [...this.exitedGroups, conversationId];
    this.requestUpdate();
  }

  handleMenuClosed() {
    this.isContextMenuOpen = false;
    this.selectedTileForMenu = null;
  }

  getConversationIdFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('conversation');
  }

  isTileSelected(tileConversationId) {
    const urlConversationId = this.getConversationIdFromURL();
    return tileConversationId === urlConversationId;
  }

  getMessageBody(tile, userMetaData) {
    const { senderId, senderName, body, attachments } = tile.lastMessageDetails;
    if (!body) return '';
    const isGroup = tile?.isGroup;
    const sender = senderId === userMetaData.userId ? 'You' : senderName || '';
    let messageContent = '';
    if (body === 'AttachmentMessage:Multiple' && attachments?.length > 0) {
      const lastAttachment = attachments[attachments.length - 1];
      if (lastAttachment?.mimeType?.includes('video')) {
        messageContent = 'Video';
      } else if (lastAttachment?.mimeType?.includes('image')) {
        messageContent = 'Image';
      } else if (lastAttachment?.mimeType?.includes('audio')) {
        messageContent = 'Audio';
      } else {
        messageContent = 'Document';
      }
    } else if (body === 'AttachmentMessage:Image') {
      messageContent = 'Image';
    } else if (body === 'AttachmentMessage:Video') {
      messageContent = 'Video';
    } else if (body === 'AttachmentMessage:File') {
      messageContent = 'Document';
    } else if (body === 'AttachmentMessage:Audio') {
      messageContent = 'Document';
    } else {
      messageContent = body;
    }
    return isGroup ? `${sender}: ${messageContent}` : messageContent;
  }

  getReactionEmoji(reactionType) {
    const emojiMap = {
      star: '⭐',
      crying_with_laughter: '😂',
      yes: '👍',
      no: '❌',
      party: '🥳',
      high_five: '✋',
      rock: '🪨',
      facepal: '🤦',
      sarcastic: '🙄',
      talking_too_much: '🤫',
      crying: '😭',
      heart: '❤️',
      surprised: '😮',
      bowing: '🙇',
    };
    return emojiMap[reactionType.toLowerCase()] || reactionType;
  }



  getLastMessageText(tile, userMetaData) {
    if (!tile?.lastMessageDetails) return '';
    const { action, initiatorId, initiatorName, memberName, userId, userName, members, memberAdded, reactionType } = tile.lastMessageDetails;
    switch (action) {
      case 'conversationCreated':
        return 'Conversation Created';
      case 'reactionRemove':
        return `${userId === userMetaData.userId ? 'You' : userName} removed ${this.getReactionEmoji(reactionType)}`;
      case 'reactionAdd':
        return `${userId === userMetaData.userId ? 'You' : userName} reacted ${this.getReactionEmoji(reactionType)}`;
      case 'removeAdmin':
        return `${initiatorId === userMetaData.userId ? 'You' : initiatorName} removed ${memberName === userMetaData.userName ? 'you' : memberName} as an admin`;
      case 'addAdmin':
        return `${initiatorId === userMetaData.userId ? 'You' : initiatorName} made ${memberName === userMetaData.userName ? 'you' : memberName} an admin`;
      case 'membersAdd':
        return `${userId === userMetaData.userId || tile?.createdBy === userMetaData.userId ? 'You' : userName} added ${(members?.[0]?.memberId === userMetaData.userId || memberAdded?.[0] === userMetaData.userName) ? 'you' : members?.[0]?.memberName || memberAdded?.[0]}`;
      case 'membersRemove':
        return `${userId === userMetaData.userId ? 'You' : userName} removed ${(members?.[0]?.memberId === userMetaData.userId || memberAdded?.[0] === userMetaData.userName) ? 'you' : members?.[0]?.memberName || memberAdded?.[0]}`;
      case 'conversationTitleUpdated':
        return 'Conversation Title Updated';
      case 'conversationImageUpdated':
        return 'Conversation Image Updated';
      case 'userBlockConversation':
        return `${initiatorId === userMetaData?.userId ? '🚫 You blocked' + ' ' + tile?.lastMessageDetails?.opponentName : '🚫' + initiatorName + ' ' + 'blocked you'}`;
      case 'userUnblockConversation':
        return `${initiatorId === userMetaData?.userId ? 'You unblocked' + ' ' + tile?.lastMessageDetails?.opponentName : initiatorName + ' ' + 'unblocked you'}`;
      default:
        return this.getMessageBody(tile, userMetaData);
    }
  }

  handleNewChatClick(e) {
    this.renderComponentName = 'NewContact';
    this.dispatchEvent(
      new CustomEvent('new-chat-clicked', {
        bubbles: true,
        composed: true,
      }),
    );
  }
  closeContactList() {
    this.renderComponentName = 'Sidebar';
    this.requestUpdate();
  }
  getMediaType(tile) {
    if (tile?.lastMessageDetails?.attachments?.length > 0) {
      const lastAttachment = tile.lastMessageDetails.attachments[tile.lastMessageDetails.attachments.length - 1];
      const mimeType = lastAttachment.mimeType;

      if (mimeType?.includes('video')) {
        return 2; // Video
      } else if (mimeType?.includes('image')) {
        return 1; // Image
      } else if (mimeType?.includes('audio')) {
        return 4; // Audio
      } else {
        return 3; // Other
      }
    }
    return 0; // No attachment
  }

  render() {

    const typesToUse = this.customTypes.length > 0 ? this.customTypes : [...new Set(this.conversationData.map(tile => tile.customType))].map(type => ({ type, label: type }));

    // Determine which conversations to show based on filters
    let conversationsToShow = this.conversationData;
    
    
    if (this.filteredConversationsFromSearchBar !== undefined && this.filteredConversationsFromSearchBar !== null) {
      // Use the filtered conversations from SearchBar tabs (including empty array for "no results")
      conversationsToShow = this.filteredConversationsFromSearchBar;
    } else if (this.searchQuery && this.searchQuery.trim()) {
      // Apply search filter if no tab filtering is active
      conversationsToShow = this.conversationData.filter(tile => {
        const searchQuery = this.searchQuery.trim().toLowerCase();
        const searchableText = tile.isGroup ? tile.conversationTitle?.toLowerCase().trim() || '' : tile.opponentDetails?.userName?.toLowerCase().trim() || '';
        return searchableText.includes(searchQuery);
      });
    } else {
      console.log('Using all conversationData:', conversationsToShow);
    }

    // Apply additional filtering for custom types if needed
    const finalFilteredConversations = conversationsToShow.filter(tile => {
      const searchQuery = this.searchQuery?.trim()?.toLowerCase();
      const tileTitle =
        this.displayTitle && tile.conversationId === this.selectedTileId ? this.displayTitle : tile.isGroup ? tile.conversationTitle : tile?.opponentDetails?.userName;

      const searchableText = tileTitle?.toLowerCase().trim() || '';
      const matchesText = !searchQuery || searchableText.includes(searchQuery);
      const matchesCustomType = this.selectedCustomType ? tile.customType === this.selectedCustomType : true;

      return matchesText && matchesCustomType;
    });
 

    const allChatsButton = html`
      <button
        class="filter-tab ${this.selectedCustomType === 'all' ? 'active' : ''}"
        style=${styleMap(this.filterTab)}
        @click="${() => this.handleTabClick('all')}"
        @mouseenter="${this.applyHoverStyles}"
        @mouseleave="${this.removeHoverStyles}"
      >
        All
      </button>
    `;

    return this.renderComponentName === 'NewContact'
      ? html`
          <new-chat-sidebar
            .closeNewChatSidebar=${() => this.closeContactList()}
            height="100%"
            width="100%"
            bgColor="var(--bg-primary-color)"
            textColor="var(--text-primary-color)"
            .nonBlockedContacts=${this.nonBlockedContacts}
            .isNoContactsFound=${this.isNoContactsFound}
            .isLoading=${this.isLoading}
            .isPaginationMember=${this.isPaginationMember}
            .isLoadingMember=${this.isLoadingMember}
            .hasMoreMembers=${this.hasMoreMembers}
          ></new-chat-sidebar>
        `
      : html`
          <div class="chat-sidebar-container" style="background-color: ${this.sidebarColor}">
            <div class="chat-sidebar-search">
              <message-search-bar
                .isSearchBar=${this.isSidebarHeaderSearch}
                label=${this.isSidebarHeaderLabel ? this.sidebarHeaderLabel || 'Messages' : ''}
                labelColor="var(--text-primary-color)"
                labelFontFamily="sans-serif"
                labelFontWeight="600"
                labelFontSize="var(--title-font-size)"
                labelFontStyle="normal  "
                searchBarIconAlt="Search Icon"
                searchBarIconWidth="30px"
                searchBarIconHeight="30px"
                borderRadius="10px"
                borderWidth="1px"
                borderColor="var(--text-primary-color)"
                padding="0px"
                margin="0px"
                width="100%"
                inputBgColor="var(--bg-secondary-color)"
                inputFontColor="var(--text-primary-color)"
                inputPlaceholder="Search messages, usernames"
                .nonBlockedContacts="${this.nonBlockedContacts}"
                inputPlaceholderColor="var(--text-primary-color)"
                @searchConversation="${this.handleSearchInput}"
                @filteredConversationsChanged="${this.handleFilteredConversationsChanged}"
                .isNoContactsFound=${this.isNoContactsFound}
                .handleNewChatClick=${() => this.handleNewChatClick()}
                .isMqttConnected=${this.isMqttConnected}
                .userMetaData=${this.userMetaData}
                .leofferAccountId=${this.leofferAccountId}
                .conversationData=${this.conversationData}
                .handleBackButtonClick=${() => this.handleBackButtonClick()}
              ></message-search-bar>
            </div>
            ${this.isChatFilter
              ? html`
                  <div class="chat-filter-tabs" style=${styleMap(this.filterTabsContainer)}>
                    ${allChatsButton}
                    ${typesToUse.map(({ type, label }) => {
                      return html`
                        <button
                          @click="${() => this.handleTabClick(type)}"
                          class="filter-tab ${this.selectedCustomType === type ? 'active' : ''}"
                          style=${styleMap(this.filterTab)}
                          @mouseenter="${this.applyHoverStyles}"
                          @mouseout="${this.removeHoverStyles}"
                        >
                          ${label}
                        </button>
                      `;
                    })}
                  </div>
                `
              : ''}
            <div class="chat-sidebar-list" @scroll="${this.handleScroll}">
              ${this.isConversationLoading
                ? html`<div class="loading-container-top">
                    <spinner-loader
                      height="15px"
                      width="15px"
                      spinTime="1s"
                      spinnerPrimaryColor="var(--bg-primary-color)"
                      spinnerSecondaryColor="var(--text-primary-color)"
                      spinnerBorderWidth="2px"
                    ></spinner-loader>
                  </div>`
                : finalFilteredConversations.length > 0
                  ? finalFilteredConversations.map(tile => {
                      const formattedMessageDate =
                        tile?.lastMessageDetails?.action === 'conversationCreated'
                          ? formatUnixTimestamp(tile?.lastMessageDetails?.createdAt || tile?.lastMessageDetails?.conversationDetails?.createdAt || tile?.createdAt)
                          : tile?.lastMessageDetails?.action === 'addAdmin' ||
                              tile?.lastMessageDetails?.action === 'removeAdmin' ||
                              tile?.lastMessageDetails?.action === 'membersAdd' ||
                              tile?.lastMessageDetails?.action === 'membersRemove'
                            ? formatUnixTimestamp(tile?.lastMessageDetails?.sentAt)
                            : tile?.lastMessageDetails?.updatedAt
                              ? formatUnixTimestamp(tile.lastMessageDetails.updatedAt)
                              : '';
                      const isSelected = this.isTileSelected(tile.conversationId);
                      const messageDateColor = tile?.unreadMessagesCount > 0 ? this.unreadCountBgColor : this.messageDateColor;

                return html`
                  <chat-tile
                    @click=${() => {
                      this.onChatTileSelected(
                        new CustomEvent('chatTileSelected', {
                          detail: {
                            ...tile,
                            conversationId: tile.conversationId
                          }
                        }));
                        this.selectedTileId = tile.conversationId;
                      }}
                      @contextmenu=${e => this.handleRightClick(e, tile)}
                      .imageSrc=${tile?.isGroup ? getProductImage(tile?.metaData?.productImage) : tile?.opponentDetails?.userProfileImageUrl}
                      .imageAlt=${(tile?.isGroup ? tile?.conversationTitle : tile?.opponentDetails?.userName) + ' profile image'}
                      .imageHeight=${this.imageHeight}
                      .imageWidth=${this.imageWidth}
                      .imageRadius=${this.imageRadius}
                      .username=${tile?.isGroup ? tile?.conversationTitle : tile?.opponentDetails?.userName}
                      .userNameFontWeight=${this.userNameFontWeight}
                      .userNameColor=${this.userNameColor}
                      .userNameFontSize=${this.usernameFontSize}
                      .chatPreview=${this.getLastMessageText(tile, this.userMetaData)}
                      .chatPreviewColor=${this.chatPreviewColor}
                      .chatPreviewFontSize=${this.chatPreviewFontSize}
                      .unreadCount=${tile?.unreadMessagesCount > 0 ? tile?.unreadMessagesCount : 0}
                      .unreadCountColor=${this.unreadCountColor}
                      .unreadCountBgColor=${this.unreadCountBgColor}
                      .unreadCountBorderRadius=${this.unreadCountBorderRadius}
                      .unreadCountFontSize=${this.unreadCountFontSize}
                      .unreadCountHeight=${this.unreadCountHeight}
                      .unreadCountWidth=${this.unreadCountWidth}
                      .messageDate=${formattedMessageDate || undefined}
                      .messageDateColor=${messageDateColor}
                      .messageDateFontSize=${this.messageDateFontSize}
                      .padding=${this.padding}
                      .bgColor=${this.sidebarColor || this.bgColor}
                      .borderWidth=${this.borderWidth}
                      .borderColor=${this.borderColor}
                      .borderStyle=${this.borderStyle}
                      .height=${this.height}
                      .width=${this.width}
                      .isSelected=${isSelected}
                      .sidebarSelectedColor=${this.sidebarSelectedColor}
                      .isSelfMessage=${tile.lastMessageDetails?.senderId === this.userMetaData.userId}
                      .isRead=${tile.isGroup ? tile.lastMessageDetails?.readByAll : tile.lastMessageDetails?.readBy?.[0]?.userId === tile.opponentDetails?.userId ? true : false}
                      .isDelivered=${tile.isGroup ? tile.lastMessageDetails?.deliveredToAll : tile.lastMessageDetails?.deliveredTo?.[0]?.userId === tile.opponentDetails?.userId ? true : false}
                      .isTyping=${this.isTyping && tile.conversationId === this.typingConversationId}
                      .imageFillColor=${'var(--bg-primary-color)'}
                      .imageTextColor=${'var(--text-primary-color)'}
                      .isBuyNow=${tile?.metaData?.purchaseType === 'BUY_DIRECT'}
                      .isMakeOffer=${tile?.metaData?.purchaseType === 'OFFER'}
                      .isExchangeOffer=${tile?.metaData?.purchaseType === 'OFFER' && tile?.metaData?.status?.includes('EXCHANGE')}
                      .offerAmount=${tile?.metaData?.currency + ' ' + tile?.metaData?.productPrice}

                    ></chat-tile>
                  `;
                })
              : html`<div class="search-placeholder" style=${styleMap({  color: 'var(--text-primary-color)' })}>
                  <div>${unsafeHTML(NoChatPlaceholder)}</div>
                     <span>No conversation found</span>
                 </div>`}
              ${this.isLoading && this.hasMoreConversations
                ? html`<div class="loading-container">
                    <spinner-loader
                      height="15px"
                      width="15px"
                      spinTime="1s"
                      spinnerPrimaryColor="var(--bg-primary-color)"
                      spinnerSecondaryColor="var(--text-primary-color)"
                      spinnerBorderWidth="2px"
                    ></spinner-loader>
                  </div>`
                : null}
            </div>

            ${this.isContextMenuOpen
              ? html`
                  <context-menu
                    .x=${this.contextMenuX}
                    .y=${this.contextMenuY}
                    .isOpen=${this.isContextMenuOpen}
                    .menuItems=${[
                      {
                        label: this.selectedTileForMenu?.isGroup && !this.exitedGroups.includes(this.selectedTileForMenu?.conversationId) ? 'Exit Group' : 'Delete Chat',
                        action: 'delete',
                        type: 'danger',
                      },
                    ]}
                    @menu-item-selected=${this.handleMenuItemSelected}
                    @menu-closed=${this.handleMenuClosed}
                  ></context-menu>
                `
              : ''}
            ${this.isConfirmationDialogOpen
              ? html`
                  <div class="confirmation-dialog-overlay">
                    <div class="confirmation-dialog">
                      <div class="confirmation-dialog-message">${this.confirmationMessage}</div>
                      <div class="confirmation-dialog-buttons">
                        <button class="confirmation-dialog-button cancel-button" @click=${() => (this.isConfirmationDialogOpen = false)}>Cancel</button>
                        <button class="confirmation-dialog-button confirm-button" @click=${this.confirmationOnConfirm}>Exit</button>
                      </div>
                    </div>
                  </div>
                `
              : ''}
          </div>
        `;
  }

  connectedCallback() {
    super.connectedCallback();

    this.handleMessageReadStatus = this.handleMessageReadStatus.bind(this);
    window.addEventListener('messageReadStatusUpdated', this.handleMessageReadStatus);

    this.handleConversationNotFound = this.handleConversationNotFound.bind(this);
    window.addEventListener('conversationNotFound', this.handleConversationNotFound);

    this.selectedTileId = this.getConversationIdFromURL();
    window.addEventListener('popstate', () => {
      this.selectedTileId = this.getConversationIdFromURL();
      this.requestUpdate();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('messageReadStatusUpdated', this.handleMessageReadStatus);

    window.removeEventListener('conversationNotFound', this.handleConversationNotFound);

    if (this.debouncedSearchHandler && typeof this.debouncedSearchHandler.cancel === 'function') {
      this.debouncedSearchHandler?.cancel();
    }
    window.removeEventListener('popstate', () => {
      this.selectedTileId = this.getConversationIdFromURL();
      this.requestUpdate();
    });
  }

  updated(changedProperties) {
    super.updated(changedProperties);
  }

  handleConversationNotFound(event) {
    this.isLoading = false;
    this.isConversationLoading = false;
    this.requestUpdate();
  }

  handleMessageReadStatus(event) {
    const { conversationId, isRead, isDelivered } = event.detail.detail;
    this.conversationData = this.conversationData.map(conversation => {
      if (conversation.conversationId === conversationId) {
        const updatedConversation = {
          ...conversation,
          lastMessageDetails: {
            ...conversation.lastMessageDetails,
            isReadByAllMembers: isRead,
            isDeliveredToAllMembers: isDelivered,
          },
        };
        return updatedConversation;
      }
      return conversation;
    });

    // Force a re-render
    this.requestUpdate();
  }
}

customElements.define('chat-sidebar', ChatSidebar);
